import React from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Category as ImportedCategory } from "../../../types/category";

// Define the local Category interface extending or matching the imported one + active flag
export interface Category extends ImportedCategory {
    active?: boolean;
    children?: Category[];
}

interface CategorySheetProps {
    selectedCategory: string; // Level 1 categoryId
    categories: Category[];
    onClose?: () => void;
}

const CategorySheet: React.FC<CategorySheetProps> = ({
    selectedCategory,
    categories,
    onClose,
}) => {
    const navigate = useNavigate();

    // 1. Find the selected Level 1 category from the list (using safe string matching)
    const mainCategory = categories.find(
        (cat) =>
            (String(cat.categoryId) === String(selectedCategory) ||
                String(cat.id) === String(selectedCategory)) &&
            (cat.level === 1 || cat.level === 0 || !cat.level)
    );

    // 2. Get Level 2 categories supporting nested children, parentCategoryId, or fallback matching
    let rawLevelTwo: Category[] = [];

    if (mainCategory && mainCategory.children && mainCategory.children.length > 0) {
        rawLevelTwo = mainCategory.children;
    } else {
        rawLevelTwo = categories.filter(
            (cat) =>
                String(cat.parentCategoryId) === String(selectedCategory) ||
                (mainCategory && String(cat.parentCategoryId) === String(mainCategory.id))
        );
    }

    // If still empty, fall back to any category sharing a parent match or level 2 matching the selector
    if (rawLevelTwo.length === 0) {
        rawLevelTwo = categories.filter(
            (cat) =>
                String(cat.parentCategoryId) === String(selectedCategory) ||
                String(cat.categoryId) === String(selectedCategory)
        );
    }

    const levelTwoCategories = rawLevelTwo.filter(
        (cat) => cat.disabled !== true && cat.active !== false
    );

    // Debugging logs placed correctly AFTER variable declarations
    console.log("Navbar selectedCategory prop:", selectedCategory);
    console.log("All categories passed to sheet:", categories);
    console.log("Matched Level 1 mainCategory:", mainCategory);
    console.log("Raw Level Two found:", rawLevelTwo);

    // 3. Get Level 3 categories supporting both nested children and flat parentCategoryId
    const getLevelThree = (subCat: Category): Category[] => {
        let subItems: Category[] = [];
        if (subCat.children && subCat.children.length > 0) {
            subItems = subCat.children;
        } else {
            subItems = categories.filter(
                (cat) =>
                    String(cat.parentCategoryId) === String(subCat.categoryId) ||
                    String(cat.parentCategoryId) === String(subCat.id)
            );
        }

        return subItems.filter(
            (cat) => cat.active !== false && cat.disabled !== false
        );
    };

    const handleCategoryNavigation = (categoryId: string) => {
        navigate(`/products/${categoryId}`);
        onClose?.();
    };

    if (levelTwoCategories.length === 0) {
        return (
            <Box
                sx={{ zIndex: 1 }}
                className="rounded-b-xl bg-white px-5 py-10 text-center"
            >
                <p className="text-sm font-semibold text-slate-700">
                    No subcategories found
                </p>
                <p className="mt-1 text-xs text-slate-400">
                    Select another category to browse products.
                </p>
            </Box>
        );
    }

    return (
        <Box
            sx={{ zIndex: 1 }}
            className="max-h-[70vh] overflow-y-auto rounded-b-xl bg-white lg:max-h-[500px]"
        >
            {/* Menu heading */}
            <div className="border-b border-slate-100 px-5 py-4 sm:px-7">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Browse categories
                </p>
            </div>

            {/* Responsive category columns */}
            <div className="grid grid-cols-2 gap-0 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {levelTwoCategories.map((item) => {
                    const subItems = getLevelThree(item);
                    const itemKey = item.categoryId || String(item.id);

                    return (
                        <div
                            key={itemKey}
                            className="min-w-0 border-b border-r border-slate-100 p-3 transition-colors hover:bg-slate-50/70 sm:p-4 lg:p-5"
                        >
                            {/* Level 2 category */}
                            <button
                                type="button"
                                onClick={() =>
                                    handleCategoryNavigation(itemKey)
                                }
                                className="mb-3 block w-full text-left text-sm font-bold leading-snug text-slate-900 transition-colors hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                            >
                                {item.name}
                                <span className="mt-1 block h-0.5 w-7 rounded-full bg-amber-400" />
                            </button>

                            {/* Level 3 category links */}
                            {subItems.length > 0 ? (
                                <ul className="space-y-2">
                                    {subItems.map((subItem) => {
                                        const subKey = subItem.categoryId || String(subItem.id);
                                        return (
                                            <li key={subKey}>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleCategoryNavigation(subKey)
                                                    }
                                                    className="block w-full break-words text-left text-xs leading-relaxed text-slate-600 transition-colors hover:text-amber-700 focus-visible:outline-none focus-visible:underline sm:text-sm"
                                                >
                                                    {subItem.name}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleCategoryNavigation(itemKey)
                                    }
                                    className="text-xs font-medium text-slate-500 transition-colors hover:text-amber-700"
                                >
                                    View products
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bottom padding */}
            <div className="h-2 bg-white" />
        </Box>
    );
};

export default CategorySheet;